import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { formatDateTime } from '../../lib/format.js';
import { Badge, Empty, ErrorBanner, SectionHead, Segmented, Stat, StatGroup, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * Phone availability: how much of the last month each phone could take a call (owner, 1 October
 * 2026: "track extensions online/offline's over 30 days, then expose it somewhere in the tms").
 *
 * One row per phone -- a desk phone, a softphone and the app on one extension are three phones,
 * because they go away separately. "Down" is offline or registered but not answering the switch's
 * pings: a caller cannot tell them apart. Time before a phone's history starts is left out of its
 * uptime rather than counted against it.
 */

type State = 'online' | 'unreachable' | 'offline' | 'never';
interface Outage { from: string; to: string | null; state: 'offline' | 'unreachable' }
interface Phone {
  reg_user: string; extension: string; name: string | null; label: string | null; user_agent: string | null;
  now: State; uptime_pct: number | null; online_sec: number; down_sec: number; unknown_sec: number;
  outage_count: number; longest_outage_sec: number; days: Array<number | null>; outages: Outage[];
}
interface Report { from: string; to: string; days: string[]; phones: Phone[] }

/** A length a person reads: 45 s, 18 min, 2 h 5 min, 3 d 4 h. */
function span(sec: number): string {
  if (sec < 60) return `${Math.max(0, Math.round(sec))} s`;
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} d ${h % 24} h` : `${d} d`;
}

/** Thresholds, not a gradient: a phone at 99.9% is fine and one at 96% lost a day. */
function tone(p: number | null): 'ok' | 'warn' | 'danger' | 'none' {
  if (p === null) return 'none';
  if (p >= 99.5) return 'ok';
  if (p >= 95) return 'warn';
  return 'danger';
}

const NOW: Record<State, { label: string; badge: 'ok' | 'warn' | 'danger' | 'muted' }> = {
  online: { label: 'Online', badge: 'ok' },
  unreachable: { label: 'Not answering', badge: 'warn' },
  offline: { label: 'Offline', badge: 'danger' },
  never: { label: 'Never registered', badge: 'muted' },
};

/**
 * The handset in a few words: its own label, else a name for what it calls itself. Zoiper says only
 * "Z 5.6.11", the app registers through our push server, and a desk phone's model is the useful part.
 */
function phoneName(p: Phone): string {
  if (p.label) return p.label;
  const ua = p.user_agent ?? '';
  if (/^Z\s/.test(ua)) return 'Zoiper';
  if (/^Push-Agent/i.test(ua)) return 'ArcSIP app';
  if (/^JsSIP/i.test(ua)) return 'Web phone';
  const yealink = /^Yealink SIP-(\S+)/i.exec(ua);
  if (yealink) return `Yealink ${yealink[1]}`;
  const fanvil = /^Fanvil (\S+)/i.exec(ua);
  if (fanvil) return `Fanvil ${fanvil[1]}`;
  return ua.replace(/\s+v?\d[\w.]*.*$/, '') || p.reg_user;
}

export function PhoneAvailabilityTab({ tenantId, timezone }: { tenantId: string; timezone: string }) {
  const [days, setDays] = useState<'7' | '30'>('30');
  const [onlyDown, setOnlyDown] = useState(false);
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const q = useQuery({
    queryKey: ['phone-availability', tenantId, days],
    queryFn: () => api.get<Report>(`/v1/tenants/${tenantId}/phone-availability?days=${days}`),
    refetchInterval: 60_000,
  });

  const phones = useMemo(() => (q.data?.phones ?? []).filter((p) => !onlyDown || p.outage_count > 0 || p.now !== 'online'),
    [q.data, onlyDown]);
  const all = q.data?.phones ?? [];
  const known = all.filter((p) => p.uptime_pct !== null);
  const totalOn = known.reduce((n, p) => n + p.online_sec, 0);
  const totalDown = known.reduce((n, p) => n + p.down_sec, 0);
  const overall = totalOn + totalDown > 0 ? Math.round((totalOn / (totalOn + totalDown)) * 1000) / 10 : null;
  const dayLabel = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });

  return (
    <>
      <SectionHead title="Phone availability"
        sub="How much of the period each phone could take a call. Offline, or registered but not answering, counts as down."
        action={<Segmented label="Period" value={days} options={[['7', '7 days'], ['30', '30 days']]} onChange={setDays} />} />

      {q.error && <ErrorBanner error={q.error} />}

      <StatGroup>
        <Stat bare label="Phones" value={all.length} icon="phone" hue={1}
          sub={`${all.filter((p) => p.now === 'online').length} online now`} />
        <Stat bare label="Uptime" value={overall === null ? '—' : `${overall}%`} icon="activity" hue={2}
          tone={overall !== null && overall < 95 ? 'danger' : overall !== null && overall < 99.5 ? 'warn' : undefined}
          sub="across every phone" />
        <Stat bare label="Outages" value={all.reduce((n, p) => n + p.outage_count, 0)} icon="warning" hue={3}
          sub={`in ${all.filter((p) => p.outage_count > 0).length} phones`} />
        <Stat bare label="Down now" value={all.filter((p) => p.now === 'offline' || p.now === 'unreachable').length}
          icon="server" hue={4}
          tone={all.some((p) => p.now === 'offline' || p.now === 'unreachable') ? 'warn' : undefined}
          sub="offline or not answering" />
      </StatGroup>

      <div className="arc-card" style={{ marginTop: 14 }}>
        <div className="arc-toolbar">
          <label className="arc-row" style={{ gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyDown} onChange={(e) => setOnlyDown(e.target.checked)} />
            Only phones with outages
          </label>
          <div className="arc-spacer" />
          <span className="arc-subtle" style={{ fontSize: 12.5 }}>
            {q.data ? `${dayLabel(q.data.days[0]!)} to today` : ''}
          </span>
        </div>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Extension</th>
                <th>Phone</th>
                <th>Now</th>
                <th className="arc-td-num">Uptime</th>
                <th className="arc-td-num">Outages</th>
                <th className="arc-td-num">Longest</th>
                <th>Each day</th>
                <th />
              </tr>
            </thead>
            {q.data === undefined ? <TableSkeleton cols={8} /> : (
              <tbody>
                {phones.map((p) => {
                  const isOpen = open.has(p.reg_user);
                  const flip = () => setOpen((s) => { const n = new Set(s); if (n.has(p.reg_user)) n.delete(p.reg_user); else n.add(p.reg_user); return n; });
                  return (
                    <Fragment key={p.reg_user}>
                      <tr className="arc-clickable" onClick={flip}>
                        <td>
                          <span className="arc-mono" style={{ marginRight: 10 }}>{p.extension}</span>
                          <span style={{ fontWeight: 550 }}>{p.name ?? ''}</span>
                        </td>
                        <td className="arc-muted arc-truncate" style={{ maxWidth: 220 }} title={p.user_agent ?? undefined}>{phoneName(p)}</td>
                        <td><Badge tone={NOW[p.now].badge}>{NOW[p.now].label}</Badge></td>
                        <td className="arc-td-num" style={{ fontWeight: 600, color: tone(p.uptime_pct) === 'none' || tone(p.uptime_pct) === 'ok' ? undefined : `var(--arc-${tone(p.uptime_pct)})` }}>
                          {p.uptime_pct === null ? <span className="arc-subtle">no data</span> : `${p.uptime_pct}%`}
                        </td>
                        <td className="arc-td-num">{p.outage_count}</td>
                        <td className="arc-td-num arc-muted">{p.longest_outage_sec > 0 ? span(p.longest_outage_sec) : '—'}</td>
                        <td>
                          <div className="arc-avail-strip" aria-label={`Uptime each day for ${p.extension}`}>
                            {p.days.map((d, i) => (
                              <span key={i} className={`arc-avail-day is-${tone(d)}`}
                                title={`${dayLabel(q.data!.days[i]!)}: ${d === null ? 'no data' : `${d}%`}`} />
                            ))}
                          </div>
                        </td>
                        <td className="arc-td-right">
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon" aria-expanded={isOpen}
                            aria-label={`${isOpen ? 'Hide' : 'Show'} outages for ${p.extension}`}
                            onClick={(e) => { e.stopPropagation(); flip(); }}>
                            <Icon name={isOpen ? 'chevronUp' : 'chevronDown'} size={13} />
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="arc-avail-detail">
                          <td colSpan={8}>
                            {p.outages.length === 0 ? (
                              <p className="arc-muted" style={{ margin: 0, fontSize: 12.5 }}>
                                {p.uptime_pct === null ? 'No history yet for this phone.' : 'No outages in this period.'}
                              </p>
                            ) : (
                              <ul className="arc-avail-outages">
                                {p.outages.map((o) => {
                                  const end = o.to ?? q.data!.to;
                                  const len = (new Date(end).getTime() - new Date(o.from).getTime()) / 1000;
                                  return (
                                    <li key={o.from}>
                                      <span className={`arc-avail-day is-${o.state === 'offline' ? 'danger' : 'warn'}`} aria-hidden="true" />
                                      <span>{o.state === 'offline' ? 'Offline' : 'Not answering'}</span>
                                      <span className="arc-muted">{formatDateTime(o.from, timezone)}</span>
                                      <span className="arc-muted">{o.to ? `to ${formatDateTime(o.to, timezone)}` : 'still down'}</span>
                                      <span className="arc-num" style={{ fontWeight: 600 }}>{span(len)}</span>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                            {p.outage_count > p.outages.length && (
                              <p className="arc-subtle" style={{ margin: '6px 0 0', fontSize: 12 }}>
                                The latest {p.outages.length} of {p.outage_count} shown.
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {q.data !== undefined && phones.length === 0 && (
          <Empty icon="phone" title={all.length === 0 ? 'No phones' : 'No outages'}
            hint={all.length === 0 ? 'Phones appear here once they are set up on an extension.' : 'Every phone stayed up in this period.'} />
        )}
      </div>
      <p className="arc-muted" style={{ fontSize: 12, marginTop: 12 }}>
        Tracked from 1 October 2026. Time before a phone's history starts is left out of its uptime.
      </p>
    </>
  );
}
